<!-- Select Existing Addresses (Multiple) -->
<app-form-section
  title="Seleccionar Direcciones"
  icon="location_on"
  color="green"
  contentClass="flex flex-col gap-4">

  <p class="text-sm text-gray-500">
    Seleccione las direcciones a las cuales se asignarán los territorios.
  </p>

  <!-- Search Field -->
  <mat-form-field appearance="outline" class="w-full">
    <mat-label>Buscar dirección</mat-label>
    <input matInput [formControl]="addressSearchCtrl" placeholder="Nombre o dirección...">
    <mat-icon matPrefix>search</mat-icon>
    @if (addressSearchCtrl.value) {
      <button matSuffix mat-icon-button (click)="clearSearch()">
        <mat-icon>clear</mat-icon>
      </button>
    }
  </mat-form-field>

  <!-- Selected Count Badge -->
  @if (modalService.selectedAddressesCount() > 0) {
    <div class="flex items-center gap-2 p-2 bg-green-50 rounded-lg border border-green-200">
      <mat-icon class="text-green-500">check_circle</mat-icon>
      <span class="text-green-700 font-medium">
        {{ modalService.selectedAddressesCount() }} dirección(es) seleccionada(s)
      </span>
    </div>
  }

  <!-- Addresses Table -->
  <div class="table-container">
    <table mat-table [dataSource]="addressesDataSource" class="w-full">
      <!-- Checkbox Column -->
      <ng-container matColumnDef="select">
        <th mat-header-cell *matHeaderCellDef class="w-12">
          <mat-checkbox
            color="primary"
            (change)="masterToggle()"
            [checked]="isAllSelected()"
            [indeterminate]="isIndeterminate()">
          </mat-checkbox>
        </th>
        <td mat-cell *matCellDef="let row">
          <mat-checkbox
            color="primary"
            (click)="$event.stopPropagation()"
            (change)="toggleRow(row)"
            [checked]="row.isSelected">
          </mat-checkbox>
        </td>
      </ng-container>

      <!-- Name Column -->
      <ng-container matColumnDef="name">
        <th mat-header-cell *matHeaderCellDef class="font-semibold">Nombre</th>
        <td mat-cell *matCellDef="let row">
          <div class="flex items-center gap-2">
            <span class="font-medium">{{ row.name }}</span>
            @if (row.isPrincipal) {
              <span class="text-xs bg-green-100 text-green-700 px-1.5 py-0.5 rounded">
                Principal
              </span>
            }
          </div>
        </td>
      </ng-container>

      <!-- Address Column -->
      <ng-container matColumnDef="address">
        <th mat-header-cell *matHeaderCellDef class="font-semibold">Dirección</th>
        <td mat-cell *matCellDef="let row">
          <span class="text-sm text-gray-600">{{ row.address }}</span>
        </td>
      </ng-container>

      <!-- Routes/Territories Column -->
      <ng-container matColumnDef="routesCount">
        <th mat-header-cell *matHeaderCellDef class="font-semibold">Territorios</th>
        <td mat-cell *matCellDef="let row">
          @if (row.routes.length > 0) {
            <div class="flex flex-wrap items-center gap-1">
              <!-- Show visible territories using computed property -->
              @for (territory of row.visibleRoutes; track territory._id) {
                <span class="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-purple-100 text-purple-800">
                  <mat-icon class="!w-3 !h-3 !text-[12px] mr-1">location_on</mat-icon>
                  {{ territory.code ? (territory.code + ' - ' + territory.name) : territory.name }}
                </span>
              }

              <!-- Badge "+N more" with mat-menu on hover -->
              @if (row.hasHiddenRoutes) {
                <span
                  class="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-gray-200 text-gray-700 cursor-pointer hover:bg-gray-300"
                  [matMenuTriggerFor]="territoriesMenu"
                  #menuTrigger="matMenuTrigger"
                  (mouseenter)="menuTrigger.openMenu()"
                  (mouseleave)="closeMenuWithDelay(menuTrigger)">
                  +{{ row.hiddenRoutesCount }} más
                </span>

                <mat-menu #territoriesMenu="matMenu" [hasBackdrop]="false" class="territories-popover">
                  <div
                    class="p-3 max-w-xs max-h-48 overflow-y-auto"
                    (mouseenter)="cancelMenuClose()"
                    (mouseleave)="menuTrigger.closeMenu()">
                    <div class="text-xs font-medium text-gray-500 mb-2">
                      Todos los territorios ({{ row.routes.length }})
                    </div>
                    <div class="flex flex-wrap gap-1">
                      @for (territory of row.routes; track territory._id) {
                        <span class="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-purple-100 text-purple-800">
                          <mat-icon class="!w-3 !h-3 !text-[12px] mr-1">location_on</mat-icon>
                          {{ territory.code ? (territory.code + ' - ' + territory.name) : territory.name }}
                        </span>
                      }
                    </div>
                  </div>
                </mat-menu>
              }
            </div>
          } @else {
            <span class="text-xs text-gray-400">Sin territorios</span>
          }
        </td>
      </ng-container>

      <tr mat-header-row *matHeaderRowDef="displayedColumns" class="bg-gray-50"></tr>
      <tr mat-row *matRowDef="let row; columns: displayedColumns;"
          (click)="toggleRow(row)"
          class="cursor-pointer hover:bg-gray-50"
          [class.bg-green-50]="row.isSelected"></tr>
    </table>

    @if (addressesDataSource.data.length === 0) {
      <div class="text-center py-8">
        <mat-icon class="text-4xl mb-2 text-orange-400">location_off</mat-icon>
        <p class="text-gray-600 font-medium mb-2">Este cliente no tiene direcciones activas</p>
        <p class="text-sm text-gray-500 mb-4">
          Para asignar territorios, primero debe crear una dirección en el perfil del cliente.
        </p>
        <button
          mat-stroked-button
          color="primary"
          (click)="navigateToClient()">
          <mat-icon svgIcon="heroicons_outline:rocket-launch" class="mr-2"></mat-icon>
          Ir al Cliente
        </button>
      </div>
    }
  </div>

  <!-- Selected Addresses Chips -->
  @if (modalService.selectedAddressesCount() > 0) {
    <div class="selected-addresses">
      <label class="text-sm font-medium text-gray-700 mb-2 block">
        Direcciones seleccionadas:
      </label>
      <mat-chip-set>
        @for (address of modalService.selectedAddresses(); track address._id) {
          <mat-chip
            [removable]="true"
            (removed)="removeFromSelection(address)">
            <mat-icon matChipAvatar class="text-green-500">location_on</mat-icon>
            {{ address.name }}
            <mat-icon matChipRemove>cancel</mat-icon>
          </mat-chip>
        }
      </mat-chip-set>
    </div>
  }
</app-form-section>

<!-- Summary of territories to assign -->
<div class="territories-summary mt-4 p-3 bg-blue-50 rounded-lg">
  <div class="flex items-center gap-2 mb-2">
    <mat-icon class="text-blue-500">checklist</mat-icon>
    <span class="font-medium text-blue-700">Territorios a asignar:</span>
  </div>
  <div class="flex flex-wrap gap-1">
    @for (territory of modalService.selectedTerritories(); track territory._id) {
      <span class="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">
        {{ territory.code ? territory.code + ' - ' + territory.name : territory.name }}
      </span>
    }
  </div>
</div>
